import z from "zod";
import { EPartnerRole, EServiceCategory, IDateOfBirth } from ".";
import { EDay, dateOfBirthSchema, updateDateOfBirthSchema } from "./date";
import {
  IGeoLocation,
  IPartnerCoordinates,
  geoLocationSchema,
  partnerCoordinatesSchema,
} from "./geo";
import { paginationMetaSchema } from "./base-api-response";
import {
  EPartnerOrderItemType,
  EPartnerOrderPaymentMethod,
  EPartnerOrderSource,
  EPartnerOrderStatus,
  EDocumentVerificationStatus,
  SLEEVE_LENGTH,
} from "../constants";

// Basic Partner Interface (for table/list view)
export interface IPartner {
  id: string;
  name: string;
  role: EPartnerRole;
  city: string;
  status: EPartnerStatus;
  photoUrl?: string;
  isOnline: boolean;
  phoneNumber?: string;
  location?: IGeoLocation;
}

// Partner Status Enum - matching backend PartnerStatus
export enum EPartnerStatus {
  ONBOARDING = "ONBOARDING",
  PENDING_VERIFICATION = "PENDING_VERIFICATION",
  ACTIVE = "ACTIVE",
  ON_HOLD = "ON_HOLD",
  BLOCKED = "BLOCKED",
}

export enum EPartnerDeactivationReason {
  NOT_RESPONDING = "NOT_RESPONDING",
  LONG_LEAVE = "LONG_LEAVE",
  SERVICE_QUALITY_ISSUE = "SERVICE_QUALITY_ISSUE",
  EMERGENCY = "EMERGENCY",
  MISBEHAVIOUR_UNETHICAL_BEHAVIOUR = "MISBEHAVIOUR_/_UNETHICAL_BEHAVIOUR",
  DOES_NOT_WANT_TO_CONTINUE = "DOES_NOT_WANT_TO_CONTINUE",
}

// Onboarding Stage Enum - matching backend OnboardingStage
export enum EOnboardingStage {
  PERSONAL_DETAILS = "PERSONAL_DETAILS",
  TRAINING = "TRAINING",
  DOCUMENTS_AND_KYC = "DOCUMENTS_AND_KYC",
  ORDER_KIT = "ORDER_KIT",
  TERMS_AND_CONDITIONS = "TERMS_AND_CONDITIONS",
  COMPLETED = "COMPLETED",
}

// Partner Leave Status Enum
export enum EPartnerLeaveStatus {
  APPLIED = "APPLIED",
  APPROVED = "APPROVED",
  REJECTED = "REJECTED",
}

export enum EDocumentType {
  PROFILE_PIC = "PROFILE_PIC",
  AADHAR = "AADHAR",
  PAN = "PAN",
  CANCELLED_CHEQUE = "CANCELLED_CHEQUE",
  SELFIE = "SELFIE",
  LEAVE_PROOF = "LEAVE_PROOF",
}

export interface IPartnerBankAccount {
  bankAccountName: string;
  bankAccountNumber: string;
  bankName: string;
  bankIfscCode: string;
}

export interface IPartnerDocuments {
  aadharNumber: string;
  aadharImageUrl: string;
  panNumber?: string | null;
  panImageUrl?: string | null;
}

export interface IPartnerEmergencyContact {
  name: string;
  relation: string;
  phone: string;
}

export interface IPartnerSensitiveData {
  dateOfBirth?: IDateOfBirth | null;
  gender?: string;
  relationWithDogs?: string;
  experienceWithDogs?: string;
  documents?: IPartnerDocuments | null;
  bankAccount?: IPartnerBankAccount | null;
  emergencyContact?: IPartnerEmergencyContact | null;
  geoLocation?: IGeoLocation | null;
  location?: IGeoLocation | null;
}

// Full Partner Response DTO (from API)
export type IPartnerResponseDto = z.infer<typeof partnerResponseDtoSchema>;

// Partner List Response
export type IPartnerListResponse = z.infer<typeof partnerListResponseSchema>;

// Search Partners Request
export interface ISearchPartnersRequest {
  page?: number;
  limit?: number;
  statuses?: EPartnerStatus[];
  serviceCategories?: EServiceCategory[];
  isOnline?: boolean;
  rating?: number; // 1-5
  hotspotCode?: string;
  hubCode?: string;
  subCityCode?: string;
  cityCode?: string;
  name?: string;
  phoneNumber?: string;
}

// Create Partner Request
export interface ICreatePartnerRequest {
  phoneNumber: string;
  name: string;
  city?: string;
  status?: EPartnerStatus;
  services?: EServiceCategory[];
  coordinates?: IPartnerCoordinates;
  hotspotId?: string;
  metadata?: Record<string, unknown>;
}

// Partner Leave Interfaces
export type IAuditUser = z.infer<typeof auditUserSchema>;
export type IPartnerLeaveResponseDto = z.infer<
  typeof partnerLeaveResponseDtoSchema
>;
export type IPartnerLeaveListResponse = z.infer<
  typeof partnerLeaveListResponseSchema
>;

export interface ICreatePartnerLeaveRequest {
  startsAt: number;
  endsAt: number;
  reason: string;
  proofUrl?: string;
}

export interface IUpdatePartnerLeaveRequest {
  startsAt?: number;
  endsAt?: number;
  reason?: string;
  proofUrl?: string;
}

export interface IApprovePartnerLeaveRequest {
  leaveIds: string[];
  comments?: string;
}

export interface IRejectPartnerLeaveRequest {
  leaveIds: string[];
  comments?: string;
}

// Partner Working Hours Interfaces
export type IPartnerWorkingHourResponseDto = z.infer<
  typeof partnerWorkingHourResponseDtoSchema
>;

// Partner Working Hour Metadata Interfaces
export interface IShiftMetadata {
  start: number;
  end: number;
}

export interface IPopularSlotMetadata {
  start: number;
  end: number;
}

export interface IServiceCategoryMetadata {
  morningShift: IShiftMetadata;
  eveningShift: IShiftMetadata;
  popularSlots: IPopularSlotMetadata[];
}

export interface IPartnerWorkingHourMetadata {
  WALKING: IServiceCategoryMetadata;
  GROOMING: IServiceCategoryMetadata;
  TRAINING: IServiceCategoryMetadata;
  PRODUCTS: IServiceCategoryMetadata;
}

export interface IPartnerWorkingHourMetadataResponse {
  success: boolean;
  statusCode: number;
  message: string;
  data: {
    path: string;
    content: IPartnerWorkingHourMetadata;
  };
}

export interface ICreatePartnerWorkingHourRequest {
  serviceCategory: EServiceCategory;
  days?: EDay[];
  slots: number[];
}

export interface IUpdatePartnerWorkingHourRequest {
  serviceCategory: EServiceCategory;
  days?: EDay[] | null;
  slots: number[]; // Required - minimum 1 slot
}

// Partner Onboarding Interfaces
export interface IInitiateOnboardingRequest {
  phoneNumber: string;
}

export type IInitiateOnboardingResponse = z.infer<
  typeof initiateOnboardingResponseSchema
>;
export type IPersonalDetailsStageData = z.infer<
  typeof personalDetailsStageDataSchema
>;
export type IOnboardingStatusResponse = z.infer<
  typeof onboardingStatusResponseSchema
>;

export interface IUpdateOnboardingRequest {
  stage: EOnboardingStage;
  [key: string]: unknown;
}

export type IUpdateStageResponse = z.infer<typeof updateStageResponseSchema>;
export type ICompleteStageResponse = z.infer<
  typeof completeStageResponseSchema
>;

export interface IBulkCompleteTrainingRequest {
  partnerIds: string[];
}

export type IBulkCompleteTrainingResponse = z.infer<
  typeof bulkCompleteTrainingResponseSchema
>;

// Partner Review Interfaces
export type IPartnerReviewCustomer = z.infer<
  typeof partnerReviewCustomerSchema
>;
export type IPartnerReviewPet = z.infer<typeof partnerReviewPetSchema>;
export type IPartnerReviewPositiveQuality = z.infer<
  typeof partnerReviewPositiveQualitySchema
>;
export type IPartnerReviewResponseDto = z.infer<
  typeof partnerReviewResponseDtoSchema
>;
export type IPartnerReviewListResponse = z.infer<
  typeof partnerReviewListResponseSchema
>;
export type IPartnerReviewStatsResponse = z.infer<
  typeof partnerReviewStatsResponseSchema
>;

// Partner Performance Interfaces
export enum EPartnerEarningModel {
  COMMISSION = "COMMISSION",
  FIXED = "FIXED",
}

export enum EPartnerPerformanceOverallStatus {
  EXCELLENT = "EXCELLENT",
  GOOD = "GOOD",
  AVERAGE = "AVERAGE",
  POOR = "POOR",
}

export type IPartnerPerformanceMetric = z.infer<
  typeof partnerPerformanceMetricSchema
>;
export type IPartnerPerformanceTargetsProgress = z.infer<
  typeof partnerPerformanceTargetsProgressSchema
>;
export type IPartnerPerformanceMetrics = z.infer<
  typeof partnerPerformanceMetricsSchema
>;
export type IPartnerPerformanceResponseDto = z.infer<
  typeof partnerPerformanceResponseDtoSchema
>;

export interface IGetPartnerPerformanceRequest {
  year?: number;
  month?: number;
}

export interface IOnboardingStageVisibility {
  isTraining: boolean;
  isDocuments: boolean;
  isKit: boolean;
  isCompleted: boolean;
}
export interface IPartnerProfileHeaderState {
  isAllStepsCompleted: boolean;
  isOnlyActiveStageLeft: boolean;
  isTrainingStage: boolean;
  isDocumentsStage: boolean;
  isKitStage: boolean;
  isTermsStage: boolean;
  isCompletedStage: boolean;
  isKitOrderPaid: boolean;
  hasCompletedTraining: boolean;
  hasCompletedDocuments: boolean;
  hasCompletedKit: boolean;
  hasCompletedTerms: boolean;
  hasMetKitRequirement: boolean;
  canMarkActive: boolean;
  shouldDisableMarkActive: boolean;
}

// ============================================================================
// ZOD SCHEMAS FOR RESPONSE DTOs
// ============================================================================

/**
 * Partner Bank Account schema
 */
const partnerBankAccountSchema = z.object({
  bankAccountName: z.string(),
  bankAccountNumber: z.string(),
  bankName: z.string(),
  bankIfscCode: z.string(),
});

/**
 * Partner Documents schema
 */
const partnerDocumentsSchema = z.object({
  aadharNumber: z.string(),
  aadharImageUrl: z.string(),
  panNumber: z.string().nullish(),
  panImageUrl: z.string().nullish(),
});

/**
 * Partner Emergency Contact schema
 */
const partnerEmergencyContactSchema = z.object({
  name: z.string(),
  relation: z.string(),
  phone: z.string(),
});

/**
 * Partner Sensitive Data schema
 */
const partnerSensitiveDataSchema = z.object({
  dateOfBirth: dateOfBirthSchema.nullish(),
  gender: z.string().nullish(),
  relationWithDogs: z.string().nullish(),
  experienceWithDogs: z.string().nullish(),
  documents: partnerDocumentsSchema.nullish(),
  bankAccount: partnerBankAccountSchema.nullish(),
  emergencyContact: partnerEmergencyContactSchema.nullish(),
  geoLocation: geoLocationSchema.nullish(),
  location: geoLocationSchema.nullish(),
});

const documentVerificationSchema = z.object({
  verificationStatus: z.nativeEnum(EDocumentVerificationStatus),
  verifiedAt: z.number().nullish(),
  verifiedBy: z.string().nullish(),
});

/**
 * Partner Response DTO schema
 */
export const partnerResponseDtoSchema = partnerSensitiveDataSchema.extend({
  id: z.string(),
  name: z.string(),
  phoneNumber: z.string(),
  email: z.string().email("Invalid email address").nullish(),
  freshDeskId: z.string().nullish(),
  city: z.string().nullish(),
  status: z.nativeEnum(EPartnerStatus),
  rating: z.number().nullish(),
  sessionsCompleted: z.number().nullish(),
  selfieImageUrl: z.string().nullish(),
  services: z.array(z.nativeEnum(EServiceCategory)).nullish(),
  coordinates: partnerCoordinatesSchema.nullish(),
  hotspotId: z.string().nullish(),
  metadata: z.record(z.unknown()).nullish(),
  onboardingStage: z.nativeEnum(EOnboardingStage),
  isOnboarded: z.boolean(),
  onboardedAt: z.number().nullish(),
  isOnlineToday: z.boolean(),
  vehicle: z.string(),
  // Document verification fields
  aadharVerification: documentVerificationSchema.nullish(),
  panVerification: documentVerificationSchema.nullish(),
  bankAccountVerification: documentVerificationSchema.nullish(),
});

/**
 * Partner List Response schema
 */
export const partnerListResponseSchema = z.object({
  items: z.array(partnerResponseDtoSchema),
  meta: paginationMetaSchema,
});

const updateDocumentVerificationSchema = z.object({
  verificationStatus: z.nativeEnum(EDocumentVerificationStatus),
});

export const updatePartnerRequestDtoSchema = z
  .object({
    name: z.string().min(1).nullish(),
    city: z.string().nullish(),
    status: z.nativeEnum(EPartnerStatus).nullish(),
    services: z.array(z.nativeEnum(EServiceCategory)).nullish(),
    coordinates: z
      .object({ latitude: z.number(), longitude: z.number() })
      .nullish(),
    hotspotId: z.string().uuid().nullish(),
    metadata: z.record(z.unknown()).nullish(),
    bankAccount: partnerBankAccountSchema.nullish(),
    emergencyContact: partnerEmergencyContactSchema.nullish(),
    checkIn: z.boolean().optional(), // Check-in flag (if true, sets appCheckInAt timestamp)
    // Document verification fields (only status is required for update)
    aadharVerification: updateDocumentVerificationSchema.nullish(),
    panVerification: updateDocumentVerificationSchema.nullish(),
    bankAccountVerification: updateDocumentVerificationSchema.nullish(),
    // new keys
    phoneNumber: z.string().nullish(),
    documents: partnerDocumentsSchema.nullish(),
    selfieImageUrl: z.string().nullish(),
    dateOfBirth: updateDateOfBirthSchema.nullish(),
    email: z.string().email("Invalid email address").nullish(),
    freshDeskId: z.string().nullish(),
    deactivationReason: z.nativeEnum(EPartnerDeactivationReason).nullish(),
  })
  .refine((data) => Object.keys(data).length > 0, {
    message: "At least one field must be provided for update",
  });

export type IUpdatePartnerRequest = z.infer<
  typeof updatePartnerRequestDtoSchema
>;

/**
 * Audit User schema
 */
const auditUserSchema = z.object({
  id: z.string(),
  name: z.string(),
  email: z.string().nullish(),
});

/**
 * Partner Leave Response DTO schema
 */
const partnerLeaveResponseDtoSchema = z.object({
  id: z.string(),
  partnerId: z.string(),
  startsAt: z.number(),
  endsAt: z.number(),
  reason: z.string().nullish(),
  status: z.nativeEnum(EPartnerLeaveStatus),
  approvedBy: auditUserSchema.nullish(),
  approvedAt: z.number().nullish(),
  approvalComments: z.string().nullish(),
  rejectedBy: auditUserSchema.nullish(),
  rejectedAt: z.number().nullish(),
  rejectionComments: z.string().nullish(),
  createdAt: z.number(),
  updatedAt: z.number(),
  createdBy: auditUserSchema.nullish(),
  updatedBy: auditUserSchema.nullish(),
});

/**
 * Partner Leave List Response schema
 */
export const partnerLeaveListResponseSchema = z.object({
  items: z.array(partnerLeaveResponseDtoSchema),
  meta: paginationMetaSchema,
});

/**
 * Partner Working Hour Response DTO schema
 */
export const partnerWorkingHourResponseDtoSchema = z.object({
  id: z.string().nullish(),
  partnerId: z.string(),
  serviceCategory: z.nativeEnum(EServiceCategory),
  days: z.array(z.nativeEnum(EDay)),
  slots: z.array(z.number()),
  createdAt: z.number().nullish(),
  updatedAt: z.number().nullish(),
});

/**
 * Initiate Onboarding Response schema
 */
export const initiateOnboardingResponseSchema = z.object({
  id: z.string(),
  partnerId: z.string(),
  currentStage: z.nativeEnum(EOnboardingStage),
  createdAt: z.number(),
});

/**
 * Personal Details Stage Data schema
 */
const personalDetailsStageDataSchema = z.object({
  vehicle: z.string().nullish(),
  serviceCategory: z.array(z.string()).nullish(),
  firstName: z.string().nullish(),
  lastName: z.string().nullish(),
  dob: dateOfBirthSchema.nullish(),
  gender: z.string().nullish(),
  location: z
    .object({
      area: z.string().nullish(),
      city: z.string().nullish(),
      state: z.string().nullish(),
      pincode: z.string().nullish(),
      coordinates: z
        .object({
          latitude: z.number().nullish(),
          longitude: z.number().nullish(),
        })
        .nullish(),
    })
    .nullish(),
  relationWithDogs: z.string().nullish(),
  experienceWithDogs: z.string().nullish(),
});

/**
 * Onboarding Status Response schema
 */
export const onboardingStatusResponseSchema = z.object({
  id: z.string(),
  partnerId: z.string(),
  currentStage: z.nativeEnum(EOnboardingStage),
  stageData: z
    .object({
      personalDetails: personalDetailsStageDataSchema.nullish(),
    })
    .passthrough(),
  completedStages: z.array(z.nativeEnum(EOnboardingStage)),
  completionTimestamps: z.record(z.number()),
  createdAt: z.number(),
  updatedAt: z.number(),
});

/**
 * Update Stage Response schema
 */
export const updateStageResponseSchema = z.object({
  success: z.boolean(),
  currentStage: z.nativeEnum(EOnboardingStage),
  updatedAt: z.number(),
});

/**
 * Complete Stage Response schema
 */
export const completeStageResponseSchema = z.object({
  success: z.boolean(),
  previousStage: z.nativeEnum(EOnboardingStage),
  currentStage: z.nativeEnum(EOnboardingStage),
  completedAt: z.number(),
});

/**
 * Bulk Complete Training Response schema
 */
export const bulkCompleteTrainingResponseSchema = z.object({
  results: z.array(
    z.object({
      id: z.string(),
      success: z.boolean(),
      data: z
        .object({
          currentStage: z.nativeEnum(EOnboardingStage),
        })
        .nullish(),
      error: z
        .object({
          code: z.string(),
          message: z.string(),
          details: z.record(z.unknown()).nullish(),
        })
        .nullish(),
    }),
  ),
  meta: z.object({
    totalRequested: z.number(),
    successCount: z.number(),
    failureCount: z.number(),
    partialSuccess: z.boolean(),
  }),
});

/**
 * Partner Review Customer schema
 */
const partnerReviewCustomerSchema = z.object({
  id: z.string(),
  name: z.string(),
  photoUrl: z.string().nullish(),
});

/**
 * Partner Review Pet schema
 */
const partnerReviewPetSchema = z.object({
  id: z.string(),
  name: z.string(),
  profilePicUrl: z.string().nullish(),
});

/**
 * Partner Review Positive Quality schema
 */
const partnerReviewPositiveQualitySchema = z.object({
  label: z.string(),
  value: z.string(),
  category: z.string(),
  isCustom: z.boolean(),
});

/**
 * Partner Review Response DTO schema
 */
const partnerReviewResponseDtoSchema = z.object({
  id: z.string(),
  rating: z.number(),
  comments: z.string().nullish(),
  positiveQualities: z.array(partnerReviewPositiveQualitySchema).nullish(),
  customer: partnerReviewCustomerSchema,
  pets: z.array(partnerReviewPetSchema).nullish(),
  createdAt: z.number(),
  updatedAt: z.number(),
});

/**
 * Partner Review List Response schema
 */
export const partnerReviewListResponseSchema = z.object({
  items: z.array(partnerReviewResponseDtoSchema),
  meta: paginationMetaSchema,
});

/**
 * Partner Review Stats Response schema
 * Aggregated rating stats computed from actual session reviews
 */
export const partnerReviewStatsResponseSchema = z.object({
  total: z.number(),
  avgRating: z.number(),
  r1: z.number(),
  r2: z.number(),
  r3: z.number(),
  r4: z.number(),
  r5: z.number(),
});

/**
 * Partner Performance Metric schema
 */
export const partnerPerformanceMetricSchema = z.object({
  actual: z.number(),
  target: z.number(),
  met: z.boolean(),
  percentage: z.number(),
  breakdown: z.record(z.unknown()),
});

/**
 * Partner Performance Targets Progress schema
 */
export const partnerPerformanceTargetsProgressSchema = z.object({
  met: z.number(),
  total: z.number(),
});

/**
 * Partner Performance Metrics schema
 */
const partnerPerformanceMetricsSchema = z.object({
  daysActive: partnerPerformanceMetricSchema,
  requestsAccepted: partnerPerformanceMetricSchema,
  sessionsCompleted: partnerPerformanceMetricSchema,
  averageRating: partnerPerformanceMetricSchema,
  onTimeSessions: partnerPerformanceMetricSchema,
});

/**
 * Partner Performance Response DTO schema
 */
export const partnerPerformanceResponseDtoSchema = z.object({
  code: z.string(),
  partnerId: z.string(),
  earningModel: z.nativeEnum(EPartnerEarningModel),
  year: z.number(),
  month: z.number(),
  overallStatus: z.nativeEnum(EPartnerPerformanceOverallStatus),
  targetsProgress: partnerPerformanceTargetsProgressSchema,
  metrics: partnerPerformanceMetricsSchema,
  calculatedAt: z.string(),
  calculationDate: z.string(),
});

/**
 * Partner Order Delivery Address schema
 */
const partnerOrderDeliveryAddressSchema = z.object({
  area: z.string(),
  city: z.string(),
  state: z.string().nullish(),
  pincode: z.string().nullish(),
  houseNumber: z.string(),
  floor: z.string().nullish(),
  society: z.string().nullish(),
  landmark: z.string().nullish(),
});

export type IPartnerOrderDeliveryAddressDo = z.infer<
  typeof partnerOrderDeliveryAddressSchema
>;

/**
 * Partner Order Item Response DTO schema
 */
const partnerOrderItemResponseDtoSchema = z.object({
  id: z.string(),
  itemType: z.nativeEnum(EPartnerOrderItemType),
  itemName: z.string(),
  quantity: z.number(),
  size: z.string().nullish(),
  sleeveLength: z.nativeEnum(SLEEVE_LENGTH).nullish(),
  sku: z.string().nullish(),
  unitPrice: z.number().nullish(),
  totalPrice: z.number().nullish(),
  metadata: z.record(z.unknown()).nullish(),
});

/**
 * Partner Order Response DTO schema
 */
export const partnerOrderResponseDtoSchema = z.object({
  id: z.string(),
  partnerId: z.string(),
  orderType: z.nativeEnum(EPartnerOrderItemType),
  orderStatus: z.nativeEnum(EPartnerOrderStatus),
  source: z.nativeEnum(EPartnerOrderSource),
  onboardingId: z.string().nullish(),
  orderNumber: z.string(),
  deliveryAddress: partnerOrderDeliveryAddressSchema,
  paymentReference: z.string().nullish(),
  paymentMethod: z.nativeEnum(EPartnerOrderPaymentMethod).nullish(),
  paidAt: z.number().nullish(),
  paidBy: z.string().nullish(),
  trackingNumber: z.string().nullish(),
  trackingUrl: z.string().nullish(),
  shippedAt: z.number().nullish(),
  deliveredAt: z.number().nullish(),
  cancelledAt: z.number().nullish(),
  cancellationReason: z.string().nullish(),
  notes: z.string().nullish(),
  items: z.array(partnerOrderItemResponseDtoSchema),
  metadata: z.record(z.unknown()).nullish(),
  createdAt: z.number(),
  updatedAt: z.number(),
  createdBy: z.string(),
  updatedBy: z.string(),
});

// ============================================================================
// EXISTING SCHEMAS
// ============================================================================

/**
 * Partner search request schema
 */
export const partnerSearchRequestSchema = z.object({
  query: z.string().trim().min(1),
  page: z.number().int().positive().optional(),
  limit: z.number().int().positive().optional(),
});

/**
 * Partner search response item schema
 * Contains basic partner information for search results
 */
export const partnerSearchItemSchema = z.object({
  id: z.string(),
  name: z.string(),
  phoneNumber: z.string(),
  email: z.string().nullable(),
});

/**
 * Partner search response schema
 * Contains paginated list of partner search results
 */
export const partnerSearchResponseSchema = z.object({
  items: z.array(partnerSearchItemSchema),
  meta: paginationMetaSchema,
});

// Type exports
export type IPartnerSearchRequest = z.infer<typeof partnerSearchRequestSchema>;
export type IPartnerSearchItem = z.infer<typeof partnerSearchItemSchema>;
export type IPartnerSearchResponse = z.infer<
  typeof partnerSearchResponseSchema
>;

/**
 * partner schema
 */
export const partnerSchema = z.object({
  id: z.string(),
  name: z.string(),
  rating: z.number().optional(),
  sessionsCompleted: z.number().optional(),
  selfieImageUrl: z.string().optional(),
  phoneNumber: z.string(),
  city: z.string().optional(),
  status: z.string(),
  onboardedAt: z.number().optional(),
  services: z.array(z.string()).optional(),
  metadata: z.record(z.unknown()).optional(),
  onboardingStage: z.string().optional(),
  dateOfBirth: z
    .object({
      year: z.number(),
      month: z.number(),
      date: z.number(),
    })
    .optional(),
  isOnboarded: z.boolean().optional(),
  isOnlineToday: z.boolean().optional(),
  appCheckInAt: z.number().optional(),
  location: geoLocationSchema.nullish(),
});

export type IPartnerSchema = z.infer<typeof partnerSchema>;

/**
 * Partner basic details schema
 * Used in session assignment request responses
 */
export const partnerBasicDetailsSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  phoneNumber: z.string().nullable(),
  selfieImageUrl: z.string().url().nullable(),
});

export type IPartnerBasicDetails = z.infer<typeof partnerBasicDetailsSchema>;

/**
 * Mark order as paid request schema
 */
export const markPaidRequestSchema = z.object({
  paymentReference: z.string().min(1, "Payment reference is required"),
  paymentMethod: z.nativeEnum(EPartnerOrderPaymentMethod).nullish(),
  notes: z.string().nullish(),
});

export type IMarkPaidRequest = z.infer<typeof markPaidRequestSchema>;

/**
 * Order item response DTO
 */
export type IPartnerOrderItemResponseDto = z.infer<
  typeof partnerOrderItemResponseDtoSchema
>;

/**
 * Order response DTO
 */
export type IPartnerOrderResponseDto = z.infer<
  typeof partnerOrderResponseDtoSchema
>;
